import { useQuery } from '@tanstack/react-query'
import { ChevronsUpDown } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import type { Property } from '@rushdb/javascript-sdk'
import { INDEX_NAME_PATTERN, type EmbeddingIndex } from '~/features/indexes/types'
import type { Project } from '~/features/projects/types'

import { Button } from '~/elements/Button'
import { Card, CardBody, CardHeader } from '~/elements/Card'
import { Dialog, DialogTitle } from '~/elements/Dialog'
import { TextField } from '~/elements/Input'
import { SearchSelect, SelectItem } from '~/elements/SearchSelect'
import { Skeleton } from '~/elements/Skeleton'
import { LabelName } from '~/features/labels/components/LabelName'
import { api } from '~/lib/api'
import { cn } from '~/lib/utils'

import { useAddIndexMutation } from '../hooks/useIndexMutations'
import { useProjectLabelsQuery } from '~/features/projects/hooks/useProjectQueries'

/** The string properties of `label`, once each. */
export function useLabelPropertiesQuery(label: string) {
  return useQuery({
    queryKey: ['index-properties', label],
    queryFn: async () => {
      const result = await api.properties.find({
        searchQuery: { labels: [label] },
        init: {} as RequestInit
      })

      return result.data
        .filter((property) => property.type === 'string')
        .filter(
          (property, index, all) => all.findIndex((candidate) => candidate.name === property.name) === index
        )
    },
    enabled: !!label
  })
}

function isIndexed(existingIndexes: EmbeddingIndex[] | undefined, label: string, propertyName: string) {
  return (
    existingIndexes?.some(
      (index) =>
        index.sourceType === 'managed' && index.label === label && index.propertyName === propertyName
    ) ?? false
  )
}

export function AddIndexCard({
  existingIndexes,
  projectId
}: {
  existingIndexes?: EmbeddingIndex[]
  projectId: Project['id']
}) {
  return (
    <Card>
      <CardHeader title="Create semantic index" />
      <CardBody>
        <AddIndexForm existingIndexes={existingIndexes} projectId={projectId} />
      </CardBody>
    </Card>
  )
}

export function AddIndexDialog({
  existingIndexes,
  initialLabel,
  initialPropertyName,
  onCreated,
  projectId,
  trigger
}: {
  existingIndexes?: EmbeddingIndex[]
  /** Preselected label and property: the dialog opens at the naming step. */
  initialLabel?: string
  initialPropertyName?: string
  onCreated?: () => void
  projectId: Project['id']
  trigger: React.ReactNode
}) {
  const [open, setOpen] = useState(false)

  return (
    <Dialog className="sm:max-w-2xl" onOpenChange={setOpen} open={open} trigger={trigger}>
      <DialogTitle>Create semantic index</DialogTitle>
      <p className="mt-2 text-content2">
        Choose a label and string property to make that field searchable by meaning. Managed indexes embed
        matching records and continue indexing new writes.
      </p>
      {open && (
        <AddIndexForm
          className="mt-6"
          existingIndexes={existingIndexes}
          initialLabel={initialLabel}
          initialPropertyName={initialPropertyName}
          modalSelect
          onCreated={() => {
            setOpen(false)
            onCreated?.()
          }}
          projectId={projectId}
        />
      )}
    </Dialog>
  )
}

/** Client-side check of a chosen name; the server enforces the same rules. */
export function indexNameProblem(name: string, takenNames: string[]): string | undefined {
  if (!INDEX_NAME_PATTERN.test(name) || name.startsWith('emb__')) {
    return 'Use 1–64 letters, digits, ".", "_", ":" or "-" (not starting with "emb__").'
  }
  if (takenNames.includes(name)) {
    return 'This project already has an index with this name.'
  }
  return undefined
}

function AddIndexForm({
  className,
  existingIndexes,
  initialLabel = '',
  initialPropertyName = '',
  modalSelect = false,
  onCreated,
  projectId
}: {
  className?: string
  existingIndexes?: EmbeddingIndex[]
  initialLabel?: string
  initialPropertyName?: string
  modalSelect?: boolean
  onCreated?: () => void
  projectId: Project['id']
}) {
  const { mutateAsync: mutate, error } = useAddIndexMutation()

  const { data: labels, isPending: loadingLabels } = useProjectLabelsQuery()

  const [label, setLabel] = useState<string>(initialLabel)
  const [propertyName, setPropertyName] = useState<string>(initialPropertyName)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Second step: the index name, prefilled with the server's default for the
  // chosen label and property (`<label>.<property>.<model>.<dims>.<similarity>`).
  const preselected = initialLabel.length > 0 && initialPropertyName.length > 0
  const [step, setStep] = useState<'select' | 'name'>(preselected ? 'name' : 'select')
  const [name, setName] = useState('')
  const nameSuggestion = useQuery({
    queryKey: ['index-name-suggestion', projectId, label, propertyName],
    queryFn: () => api.indexes.suggestName({ projectId, label, propertyName }),
    enabled: step === 'name' && label.length > 0 && propertyName.length > 0,
    staleTime: 0
  })
  useEffect(() => {
    if (nameSuggestion.data?.name) {
      setName(nameSuggestion.data.name)
    }
  }, [nameSuggestion.data])
  const nameProblem =
    step === 'name' && name.length > 0 ?
      indexNameProblem(name, existingIndexes?.map((index) => index.name) ?? [])
    : undefined
  const {
    data: properties = [],
    isPending: propsPending,
    isFetching: propsFetching
  } = useLabelPropertiesQuery(label)
  const propsLoading = label.length > 0 && (propsPending || propsFetching)
  const availableProperties = useMemo(
    () => properties.filter((property) => !isIndexed(existingIndexes, label, property.name)),
    [existingIndexes, label, properties]
  )
  const labelEntries = useMemo(() => Object.entries(labels ?? {}), [labels])
  const selectedLabelIdx = useMemo(
    () => labelEntries.findIndex(([labelName]) => labelName === label),
    [labelEntries, label]
  )

  useEffect(() => {
    if (label !== initialLabel) {
      setPropertyName('')
    }
  }, [label, initialLabel])

  useEffect(() => {
    if (
      step === 'select' &&
      !propsLoading &&
      propertyName &&
      !availableProperties.some((property) => property.name === propertyName)
    ) {
      setPropertyName('')
    }
  }, [availableProperties, propertyName, propsLoading, step])

  const isValid = label.length > 0 && propertyName.length > 0
  const canCreate = isValid && name.length > 0 && !nameProblem && !nameSuggestion.isFetching

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (step === 'select') {
      if (isValid) {
        setName('')
        setStep('name')
      }
      return
    }
    if (!canCreate) return
    setIsSubmitting(true)
    try {
      await mutate({ projectId, name, label, propertyName })
      setLabel('')
      setPropertyName('')
      setName('')
      setStep('select')
      onCreated?.()
    } catch {
      // surfaced via the mutation's onError toast and the inline error below
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className={cn('flex flex-col gap-5', className)} onSubmit={handleSubmit}>
      {step === 'name' ?
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <LabelName idx={Math.max(selectedLabelIdx, 0)} label={label} />
            <span className="text-content3">:</span>
            <span className="w-fit rounded-sm bg-content3/10 px-1 font-mono text-xs text-content2">
              {propertyName}
            </span>
          </div>
          <TextField
            autoFocus
            caption="Unique within this project. API clients use it to list, search and write to the index."
            disabled={nameSuggestion.isFetching}
            error={
              nameProblem ?? (nameSuggestion.isError ? 'Could not suggest a name; enter one.' : undefined)
            }
            label="Index name"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder={nameSuggestion.isFetching ? 'Suggesting a name…' : 'Index name'}
            size="small"
            value={name}
          />
        </div>
      : <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Label</span>
            {loadingLabels ?
              <Skeleton enabled>
                <Button className="w-full" size="small" variant="outline">
                  Loading…
                </Button>
              </Skeleton>
            : <SearchSelect
                modal={modalSelect}
                trigger={
                  <Button
                    className="w-full justify-between font-normal"
                    size="small"
                    type="button"
                    variant="outline"
                  >
                    <span className={label ? '' : 'text-content3'}>
                      {label ?
                        <LabelName idx={Math.max(selectedLabelIdx, 0)} label={label} />
                      : 'Select a label'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                }
              >
                {labelEntries.map(([labelName], idx) => (
                  <SelectItem key={labelName} onSelect={() => setLabel(labelName)} value={labelName}>
                    <LabelName idx={idx} label={labelName} />
                  </SelectItem>
                ))}
              </SearchSelect>
            }
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Property</span>
            {!label ?
              <Button
                className="w-full justify-between font-normal"
                disabled
                size="small"
                type="button"
                variant="outline"
              >
                <span className="text-content3">Select a label first</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            : propsLoading ?
              <Skeleton enabled>
                <Button className="w-full" size="small" variant="outline">
                  Loading…
                </Button>
              </Skeleton>
            : <SearchSelect
                modal={modalSelect}
                trigger={
                  <Button
                    className="w-full justify-between font-normal"
                    size="small"
                    type="button"
                    variant="outline"
                  >
                    <span className={propertyName ? '' : 'text-content3'}>
                      {propertyName || 'Select a text property'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                }
              >
                {availableProperties.length === 0 && label && !propsLoading ?
                  <SelectItem disabled value="">
                    No text properties found
                  </SelectItem>
                : availableProperties.map((property: Property) => (
                    <SelectItem
                      key={property.id}
                      onSelect={() => setPropertyName(property.name)}
                      value={property.name}
                    >
                      {property.name}
                    </SelectItem>
                  ))
                }
              </SearchSelect>
            }
          </div>
        </div>
      }

      {error != null && (
        <div className="text-sm text-red-500">
          {error instanceof Error ? error.message : 'Failed to create index'}
        </div>
      )}

      <div className="flex justify-end gap-2 border-t pt-5">
        {step === 'name' && !preselected && (
          <Button onClick={() => setStep('select')} type="button" variant="outline">
            Back
          </Button>
        )}
        {step === 'select' ?
          <Button disabled={!isValid} type="submit" variant="primary">
            Next
          </Button>
        : <Button disabled={!canCreate} loading={isSubmitting} type="submit" variant="primary">
            Create
          </Button>
        }
      </div>
    </form>
  )
}
